"use client";
import { revealFlowInput } from "@/components/flow/flow-focus";

import {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

/** The landing page stays intact; only the private, interactive flow is modal. */
export function ValuationPublicDialog({
  open,
  onOpenChange,
  children,
  triggerRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const [viewport, setViewport] = useState<CSSProperties>({});
  useEffect(() => {
    if (!open) return;
    const visual = window.visualViewport;
    if (!visual) return;
    let frame = 0;
    const update = () => {
      if (visual.scale !== 1) return;
      setViewport({
        "--flow-viewport-height": `${visual.height}px`,
        top: `${visual.offsetTop}px`,
      } as CSSProperties);
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(revealFlowInput);
    };
    update();
    visual.addEventListener("resize", update);
    visual.addEventListener("scroll", update);
    return () => {
      cancelAnimationFrame(frame);
      visual.removeEventListener("resize", update);
      visual.removeEventListener("scroll", update);
    };
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-valuation-flow
        style={viewport}
        disableAnimation
        className="inset-x-0 top-0 flex h-[var(--flow-viewport-height,100dvh)] max-h-[var(--flow-viewport-height,100dvh)] max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:rounded-none [&_[data-flow-presentation=dialog]>div:first-child]:pr-12"
        overlayClassName="bg-black/40 backdrop-blur-sm"
        closeButtonClassName="right-2 top-2 z-10 flex size-11 items-center justify-center rounded-lg"
        onCloseAutoFocus={(event) => {
          if (triggerRef.current?.isConnected) {
            event.preventDefault();
            triggerRef.current.focus({ preventScroll: true });
          }
        }}
      >
        <DialogTitle className="sr-only">Free Business Valuation</DialogTitle>
        <DialogDescription className="sr-only">
          Answer a few questions to get a starting estimate for your business.
        </DialogDescription>
        {children}
      </DialogContent>
    </Dialog>
  );
}
